import { RadialWheel } from './radialwheel';
import { seenPlayers } from '../game/seenPlayers';
import { SK, btnCss, panelCss, titleCss, injectSkinCss, redHover, rowHover } from './skin';

// BASE CLAIM ownership menu — the #7 hold consumer. A short TAP on a base claim opens its upkeep chest (as before);
// only a HOLD opens this. It reuses the build/hammer RADIAL WHEEL format with four options:
//   Claim Ownership  -- take an unowned + unlocked claim (automatic when you place your own)
//   Add User         -- authorize a teammate by name -> owner-level access on this claim (locked doors/chests/boxes)
//   Remove User      -- revoke an authorized teammate
//   Clear Ownership  -- relinquish your ownership (the base becomes unowned / takeable)
// Add + Remove open a small text-input / list sub-panel. main applies each pick through the Backend authority and
// relocks the first-person pointer when the menu fully closes (onClose).

export type ClaimAction = 'claim' | 'add' | 'remove' | 'clear' | 'doors';

export interface ClaimMenuState {
  you: string;
  author: string;
  owner: string | null;
  members: string[]; // authorized teammates (the Remove list)
}

// A 4-option wheel (no swatches). The pick callback fires on click-a-segment; release-on-empty dismisses (base class).
class ClaimWheel extends RadialWheel<ClaimAction, never> {
  constructor(private readonly pick: (a: ClaimAction) => void) {
    super({
      segments: [
        { key: 'claim', label: 'Claim\nOwnership' },
        { key: 'add', label: 'Add\nUser' },
        { key: 'remove', label: 'Remove\nUser' },
        { key: 'doors', label: 'Auto-Close\nDoors' },
        { key: 'clear', label: 'Clear\nOwnership' },
      ],
      activeSeg: null,
      hubFallbackLabel: 'BASE\nCLAIM',
      onPointerCommit: () => this.commitSelection(), // no auto-hide: the menu drives close/transition
    });
  }

  // Apply the hovered action (the menu's pick() drives hide/transition). Also called on E-release.
  commitSelection(): void { this.commit({ onSeg: (a) => this.pick(a), stickySeg: false }); }
}

// The add/remove sub-panel is an in-game glass window; buttons come straight off the skin fragments
// (primary = the ONE accent action per view, danger = a semantic outline, never a solid red fill).
const CARD_CSS = [
  'min-width:320px', 'max-width:420px', panelCss('glass'),
  'padding:20px 22px', 'box-shadow:0 18px 50px rgba(0,0,0,0.5)', `color:${SK.text}`,
  `font-family:${SK.font}`,
].join(';');
const TITLE_CSS = titleCss(16);
const SUB_CSS = `font-size:${SK.fsSmall};line-height:1.45;color:${SK.dim};margin-top:6px;`;
const INPUT_CSS = `width:100%;margin-top:14px;padding:9px 11px;background:${SK.well};border:1px solid ${SK.border};border-radius:${SK.rSm};color:${SK.textHi};font:14px ${SK.font};box-sizing:border-box;`;
const BTN_CSS = `${btnCss('secondary')};flex:1`;
const BTN_PRIMARY = `${btnCss('primary')};flex:1`;
const BTN_DANGER = btnCss('danger', true);
const ROW_CSS = `display:flex;align-items:center;justify-content:space-between;gap:10px;padding:7px 10px;background:${SK.surface1};border:1px solid ${SK.border};border-radius:${SK.rSm};`;

function el(tag: string, css: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  e.style.cssText = css;
  if (text != null) e.textContent = text;
  return e;
}

export class BaseclaimMenu {
  onClaim: (() => void) | null = null;
  onClear: (() => void) | null = null;
  onAddUser: ((name: string) => void) | null = null;
  onRemoveUser: ((name: string) => void) | null = null;
  onToggleDoors: (() => void) | null = null; // base-wide door auto-close flip (owner QoL)
  onClose: (() => void) | null = null; // main relocks the FP pointer here

  private readonly wheel: ClaimWheel;
  private readonly backdrop: HTMLDivElement; // dark overlay hosting the add/remove sub-panels
  private readonly card: HTMLDivElement;
  private state: ClaimMenuState | null = null;
  private active = false;
  private suppressFinish = false; // set while we hide the wheel to TRANSITION to a panel (so onHide doesn't finish)

  constructor() {
    injectSkinCss();
    this.wheel = new ClaimWheel((a) => this.pick(a));
    this.wheel.onHide = () => { if (!this.suppressFinish) this.finish(); }; // empty-space dismiss / claim / clear -> close out

    this.backdrop = document.createElement('div');
    this.backdrop.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:19', 'display:none', 'align-items:center', 'justify-content:center',
      `background:${SK.scrim}`,
    ].join(';');
    this.card = document.createElement('div');
    this.card.style.cssText = CARD_CSS;
    this.backdrop.appendChild(this.card);
    this.backdrop.addEventListener('pointerdown', (e) => { if (e.target === this.backdrop) this.finish(); }); // click outside the card -> close
    document.body.appendChild(this.backdrop);

    window.addEventListener('keydown', (e) => { if (this.active && e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); this.finish(); } }, true);
  }

  get isOpen(): boolean { return this.active; }

  // E-release while the WHEEL page is up (release-to-select): commit the hovered action, or close if
  // nothing is hovered. No-op while an add/remove sub-panel is showing (those are click-driven forms).
  releaseCommit(): void {
    if (!this.active || !this.wheel.open) return;
    if (this.wheel.hasHover) this.wheel.commitSelection();
    else this.finish();
  }

  // Show the wheel for a claim. `state` drives the Remove-User list; main has already gated who may open this.
  open(state: ClaimMenuState): void {
    this.state = state;
    this.active = true;
    this.hidePanel();
    this.wheel.show();
  }

  close(): void { this.finish(); }

  private pick(a: ClaimAction): void {
    if (a === 'claim') { this.onClaim?.(); this.wheel.hide(); return; }
    if (a === 'clear') { this.onClear?.(); this.wheel.hide(); return; }
    if (a === 'doors') { this.onToggleDoors?.(); this.wheel.hide(); return; }
    this.suppressFinish = true; this.wheel.hide(); this.suppressFinish = false; // transition, don't finish
    if (a === 'add') this.openAdd(); else this.openRemove();
  }

  private openAdd(): void {
    this.card.replaceChildren();
    const input = el('input', INPUT_CSS) as HTMLInputElement;
    input.type = 'text'; input.placeholder = 'search or type a username'; input.maxLength = 24;
    // Scrollable, SEARCHABLE list of players who have connected (from seenPlayers) -- click one to authorize,
    // so you never have to hand-type a weird username. Typing filters the list AND doubles as manual entry.
    const list = el('div', 'display:flex;flex-direction:column;gap:4px;margin-top:10px;max-height:200px;overflow:auto;');
    const submitName = (v: string): void => { const n = v.trim(); if (n) this.onAddUser?.(n); this.finish(); };
    const renderList = (): void => {
      list.replaceChildren();
      const names = seenPlayers(input.value).filter((n) => n !== this.state?.owner && !this.state?.members.includes(n));
      if (!names.length) { list.append(el('div', SUB_CSS, input.value.trim() ? 'No match — type a name then Add.' : 'No other players seen yet — type a name then Add.')); return; }
      for (const name of names) {
        const rowEl = el('div', `${ROW_CSS}cursor:pointer;`);
        rowEl.append(el('span', `color:${SK.text};font-size:14px;`, name), el('span', `color:${SK.dim};font-size:12px;`, 'Add ▸'));
        rowHover(rowEl);
        rowEl.addEventListener('click', () => submitName(name));
        list.append(rowEl);
      }
    };
    const row = el('div', 'display:flex;gap:8px;margin-top:14px;');
    const add = el('button', BTN_PRIMARY, 'Add typed name');
    const cancel = el('button', BTN_CSS, 'Cancel');
    redHover(cancel);
    add.addEventListener('click', () => submitName(input.value));
    cancel.addEventListener('click', () => this.finish());
    input.addEventListener('input', renderList);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); submitName(input.value); } });
    row.append(add, cancel);
    this.card.append(
      el('div', TITLE_CSS, 'Authorize a teammate'),
      el('div', SUB_CSS, 'Pick a player who has connected, or type a name. They get owner access on this base — its locked doors, chests and boxes — while inside the claim.'),
      input, list, row,
    );
    this.backdrop.style.display = 'flex';
    renderList();
    input.focus();
  }

  private openRemove(): void {
    this.card.replaceChildren();
    const members = [...(this.state?.members ?? [])];
    const list = el('div', 'display:flex;flex-direction:column;gap:6px;margin-top:12px;max-height:240px;overflow:auto;');
    const render = (): void => {
      list.replaceChildren();
      if (!members.length) { list.append(el('div', SUB_CSS, 'No authorized users.')); return; }
      for (const name of members) {
        const rowEl = el('div', ROW_CSS);
        rowEl.append(el('span', `color:${SK.text};font-size:14px;`, name));
        const rm = el('button', BTN_DANGER, 'Remove');
        rm.addEventListener('click', () => { this.onRemoveUser?.(name); const i = members.indexOf(name); if (i >= 0) members.splice(i, 1); render(); });
        rowEl.append(rm);
        list.append(rowEl);
      }
    };
    render();
    const done = el('button', BTN_PRIMARY, 'Done'); done.style.marginTop = '14px';
    done.addEventListener('click', () => this.finish());
    this.card.append(el('div', TITLE_CSS, 'Authorized users'), list, done);
    this.backdrop.style.display = 'flex';
  }

  private hidePanel(): void { this.backdrop.style.display = 'none'; this.card.replaceChildren(); }

  // Fully close: hide the wheel + any panel and hand control back (main relocks the pointer). Idempotent.
  private finish(): void {
    if (!this.active) return;
    this.active = false;
    this.suppressFinish = true; this.wheel.hide(); this.suppressFinish = false;
    this.hidePanel();
    this.onClose?.();
  }
}
